import {
  clamp,
  dayBounds,
  gapContaining,
  snap,
  snapDown,
  snapUp,
  type CreateOptions,
  type GeometryOptions,
  type Span,
} from './span';

/** Whether wall minute `min` lies inside one of `others` ([startMin, endMin), 2026-10b §3.3). */
function insideBlock(min: number, others: readonly Span[]): boolean {
  return others.some((other) => other.startMin <= min && min < other.endMin);
}

/** Effective default duration: snapped to the step, at least one step and the minimum duration. */
function defaultDuration(options: CreateOptions): number {
  return Math.max(snap(options.defaultDurationMin, options.step), options.step, options.minDuration);
}

/**
 * The block created by clicking at `clickMin`. The criterion is whether the pointer minute lies
 * inside a block, by the half-open [start, end) (2026-10b §3.3) — not whether a grid cell is
 * occupied, so a neighbour ending off the grid (14:10) is never silently overlapped. A blank
 * click (inside no block) is held inside its free gap: the start is the snapped-down click, not
 * before the gap start; the end is cut at the next block's start — cutting wins over the
 * minimum duration. A click inside a block (through the create band) overlays it: the start is
 * the snapped-down click held at the containing block's start, the full default duration, cut
 * only at the day end. Returns null only when the click lies outside the personal day.
 */
export function blockFromClick(
  clickMin: number,
  options: CreateOptions,
  others: readonly Span[],
): Span | null {
  const bounds = dayBounds(options.dayStartMin);
  if (clickMin < bounds.startMin || clickMin >= bounds.endMin) return null;
  const duration = defaultDuration(options);
  const hosts = others.filter((other) => other.startMin <= clickMin && clickMin < other.endMin);
  if (hosts.length > 0) {
    // Overlay creation: snapping down must not push the new block past the host's start onto
    // an earlier neighbour (§3.3), so the start is held at the latest containing start.
    const floor = Math.max(...hosts.map((host) => host.startMin));
    const startMin = Math.max(snapDown(clickMin, options.step), floor, bounds.startMin);
    return { startMin, endMin: Math.min(startMin + duration, bounds.endMin) };
  }
  const gap = gapContaining(clickMin, others, bounds);
  if (gap === null) return null;
  const startMin = Math.max(snapDown(clickMin, options.step), gap.startMin);
  return { startMin, endMin: Math.min(startMin + duration, gap.endMin) };
}

/**
 * The block created by pressing at `anchorMin` and dragging to `currentMin` (either direction):
 * it covers every step cell the drag touches — [snapDown(low), snapUp(high)] — and each end is
 * then handled by where its pointer minute lies (2026-10b §3.3): an end in blank space is held
 * inside its free gap (start not before the previous block's end, end not past the next block's
 * start); an end inside a block keeps its snapped value and overlaps on purpose. A result
 * shorter than the minimum duration extends towards the pointer, but the extension never
 * invades a neighbouring block: a gap shorter than the minimum duration cuts it short. With
 * `currentExact`, the pointer end keeps the exact `currentMin` (a neighbour detent hit, §3.6)
 * instead of snapping. Returns null when the anchor lies outside the personal day, or when no
 * non-empty block fits at the day edge the drag was clamped to.
 */
export function blockFromDrag(
  anchorMin: number,
  currentMin: number,
  options: GeometryOptions,
  others: readonly Span[] = [],
  currentExact = false,
): Span | null {
  const bounds = dayBounds(options.dayStartMin);
  if (anchorMin < bounds.startMin || anchorMin >= bounds.endMin) return null;
  const forward = currentMin >= anchorMin;
  const low = clamp(Math.min(anchorMin, currentMin), bounds.startMin, bounds.endMin);
  const high = clamp(Math.max(anchorMin, currentMin), bounds.startMin, bounds.endMin);
  let startMin = snapDown(low, options.step);
  let endMin = snapUp(high, options.step);
  if (currentExact) {
    if (forward) endMin = high;
    else startMin = low;
  }
  const startGap = insideBlock(low, others) ? null : gapContaining(low, others, bounds);
  if (startGap !== null) startMin = Math.max(startMin, startGap.startMin);
  const endGap = insideBlock(high, others) ? null : gapContaining(high, others, bounds);
  if (endGap !== null) endMin = Math.min(endMin, endGap.endMin);
  if (endMin - startMin < options.minDuration) {
    // Extend towards the pointer to the minimum duration. The extension never invades a block
    // (cutting wins, §3.3): like resizeBlock's floor compensation it stops at the nearest
    // boundary in `others` beyond the fixed end, and at the personal day. The end gap alone
    // cannot give that cap — a pointer end resting exactly on a neighbour's start counts as
    // inside that neighbour under the half-open test, which leaves the gap null.
    if (forward) {
      const limits = others.map((other) => other.startMin).filter((min) => min >= endMin && min > startMin);
      endMin = Math.max(endMin, Math.min(startMin + options.minDuration, bounds.endMin, ...limits));
    } else {
      const limits = others.map((other) => other.endMin).filter((min) => min <= startMin && min < endMin);
      startMin = Math.min(startMin, Math.max(endMin - options.minDuration, bounds.startMin, ...limits));
    }
  }
  if (endMin <= startMin) {
    // The pointer was clamped to the day edge the drag started from (a backward drag past the day
    // start, a forward drag past the day end): the pointer-side extension had no room, so extend
    // towards the day interior, stopping at the nearest block. No room at all gives no block —
    // a zero-length block is never created.
    if (forward) {
      const limits = others.map((other) => other.endMin).filter((min) => min <= startMin);
      startMin = Math.max(bounds.startMin, Math.min(startMin, endMin - options.minDuration), ...limits);
    } else {
      const limits = others.map((other) => other.startMin).filter((min) => min >= endMin);
      endMin = Math.min(bounds.endMin, Math.max(endMin, startMin + options.minDuration), ...limits);
    }
    if (endMin <= startMin) return null;
  }
  return { startMin, endMin };
}

/**
 * The span a "catch up" action fills: from the exact end of the latest block before now (or the
 * day start) up to now, snapped down to the step — the gap start keeps its precise minute even
 * off the grid (2026-10b §3.3). `nowMin` is the current wall minute counted from 00:00 of the
 * personal date. Returns null when now lies outside the day or inside a block, or when less
 * than the minimum duration is free before it (a step-sized floor would re-show the button
 * every few minutes), so the span is always entirely empty.
 */
export function catchUpSpan(nowMin: number, options: GeometryOptions, others: readonly Span[]): Span | null {
  const bounds = dayBounds(options.dayStartMin);
  if (nowMin < bounds.startMin || nowMin >= bounds.endMin) return null;
  const gap = gapContaining(nowMin, others, bounds);
  if (gap === null) return null;
  const endMin = snapDown(nowMin, options.step);
  return endMin - gap.startMin >= options.minDuration ? { startMin: gap.startMin, endMin } : null;
}
